import PropTypes from 'prop-types';
import React from 'react';
import { Card } from '@outdoorly/card';
import {
  Box,
  Flex,
  Skeleton,
  StatLabel,
  Center,
  StatNumber,
  Stat,
} from '@chakra-ui/react';
import { Text } from '@outdoorly/text';
import { useQuery } from '@apollo/client';
import { Button } from '@outdoorly/button';
import dayjs from 'dayjs';
import { GET_REFERRALS } from '../../lib/graphql/profile/queries';
import { Divider } from '@outdoorly/divider';
import { makeNetworkStatus } from '../../hooks/make-network-status';

const referralTypeKeyMirror = {
  JOINED: 'JOINED',
  PURCHASED: 'PURCHASED',
};

const ReferralSummary = (props) => {
  const { created, referred, status } = props;
  return (
    <Flex
      as={'li'}
      alignItems="center"
      justifyContent="space-between"
      my="20px"
    >
      <Box>
        <Text fontWeight={'500'} color={'scheme.textPrimary'} mb={'8px'}>
          {referred?.email}
        </Text>
        <Text color="scheme.textSecondary">
          {dayjs(created).format('MMM DD, YYYY')}
        </Text>
      </Box>
      <Box>
        <Text
          fontWeight="500"
          color={'scheme.textPrimary'}
          mb="8px"
          textAlign="right"
        >
          Joined
        </Text>
        <Text size="sm" color="scheme.textSecondary">
          Credit{' '}
          {status === referralTypeKeyMirror.JOINED ? 'Pending' : 'Earned'}
        </Text>
      </Box>
    </Flex>
  );
};

ReferralSummary.propTypes = {
  created: PropTypes.string,
  referred: PropTypes.shape({
    email: PropTypes.string,
  }),
  status: PropTypes.string,
};

export const Referrals = () => {
  const { data, fetchMore, loading, networkStatus } = useQuery(GET_REFERRALS, {
    variables: {
      first: 5,
      after: null,
    },
    notifyOnNetworkStatusChange: true,
  });

  const { fetchingMore } = makeNetworkStatus(networkStatus);

  const hasNextPage = data?.me?.referrals?.pageInfo?.hasNextPage ?? false;
  const hasReferredSomeone = data?.me?.referrals?.edges?.length > 0;
  return (
    <>
      <Card boxShadow="sm" my={2} py={6}>
        <Flex height="60px" alignItems="center">
          <Box flex={1} px={8}>
            <Stat textAlign="center">
              <Skeleton isLoaded={!loading}>
                <StatNumber>{data?.me?.numJoinedReferrals || 0}</StatNumber>
              </Skeleton>
              <StatLabel color="scheme.textSecondary" fontWeight="normal">
                Joined
              </StatLabel>
            </Stat>
          </Box>
          <Divider orientation="vertical" />
          <Box flex={1} px={8}>
            <Stat textAlign="center">
              <Skeleton isLoaded={!loading}>
                <StatNumber>{data?.me?.numPurchasedReferrals || 0}</StatNumber>
              </Skeleton>
              <StatLabel color="scheme.textSecondary" fontWeight="normal">
                Made a purchase
              </StatLabel>
            </Stat>
          </Box>
        </Flex>
      </Card>
      {hasReferredSomeone && (
        <Card boxShadow="sm" py={1} my={2}>
          <ul>
            {data?.me?.referrals?.edges.map((edge, index) => {
              const { node } = edge;

              return (
                <ReferralSummary
                  key={index}
                  created={node?.created}
                  referred={node?.referred}
                  status={node?.status}
                />
              );
            })}
          </ul>
        </Card>
      )}
      {hasNextPage && (
        <Center>
          <Button
            backgroundColor="scheme.bgPrimary"
            color="scheme.textPrimary"
            isLoading={fetchingMore}
            minW="205px"
            onClick={() =>
              fetchMore({
                variables: {
                  first: 5,
                  after: data?.me?.referrals?.pageInfo?.endCursor,
                },
              })
            }
          >
            See more referrals
          </Button>
        </Center>
      )}
    </>
  );
};
